import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";

export interface FieldHelpContent {
  title: string;
  explanation: string;
  impact: string;
  example: string;
  warning?: string;
}

export interface FieldHelpProps {
  content: FieldHelpContent;
  label?: string;
}

export function FieldHelp({ content, label = "Info" }: FieldHelpProps) {
  const id = useId();
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    function handlePointerDown(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
    }
  }

  return (
    <span
      className="field-help"
      ref={rootRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        className="field-help__button"
        aria-label={`${label}: ${content.title}`}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={handleKeyDown}
      >
        i
      </button>
      {open ? (
        <span className="field-help__popover" id={id} role="tooltip">
          <strong>{content.title}</strong>
          <span>{content.explanation}</span>
          <span>{content.impact}</span>
          <span>{content.example}</span>
          {content.warning ? <em>{content.warning}</em> : null}
        </span>
      ) : null}
    </span>
  );
}
